import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import NiftiFileReader from "@pages/Operations/DataLabellingPage/NiftiFileReader";

interface Label {
  name: string;
  color: string;
}

// Dummy labels with colors
const DUMMY_LABELS: Label[] = [
  { name: "Label A", color: "#FF0000" },
  { name: "Label B", color: "#00FF00" },
  { name: "Label C", color: "#0000FF" },
  { name: "Label D", color: "#FFFF00" },
  { name: "Label E", color: "#FF00FF" },
];

// Dummy image IDs list
const DUMMY_IMAGE_IDS = ["spleen_2.nii.gz"];

const Navbar = styled.div`
  height: 75px;
  background: #f04a23;
  display: flex;
  align-items: center;
  padding: 0 24px;
  box-shadow: 0px 3px 3px -2px rgba(0,0,0,0.2), 
              0px 3px 4px 0px rgba(0,0,0,0.14), 
              0px 1px 8px 0px rgba(0,0,0,0.12);
`;

const NavbarTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 900;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const ContentWrapper = styled.div`
  display: flex;
  height: calc(100vh - 75px);
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
`;

const Sidebar = styled.div`
  width: 300px;
  border-right: 1px solid #e0e0e0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f04a23;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: 15px;
  font-weight: 800;
  text-align: center;
`;

const ImageListContainer = styled.div`
  padding: 16px;
  height: calc(100vh - 145px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const ImageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageListItem = styled.li<{ isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-radius: 8px;
  background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#ffffff'};
  border: 1px solid ${props => props.isSelected ? '#f04a23' : '#e0e0e0'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#f5f5f5'};
    border-color: #f04a23;
  }

  &::before {
    content: '';
    display: block;
    width: 24px;
    height: 24px;
    background: ${props => props.isSelected ? 'white' : '#f04a23'};
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
    opacity: ${props => props.isSelected ? '1' : '0.8'};
  }
`;

const ImageName = styled.span<{ isSelected: boolean }>`
  font-size: 13px;
  color: ${props => props.isSelected ? 'white' : '#333'};
  font-weight: ${props => props.isSelected ? 'bold' : 'normal'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
`;

const NiftiViewerContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: 10px;
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 300px;
  width: 100%;
`;

const ProgressText = styled.div`
  color: #f04a23;
  font-weight: 500;
  font-size: 14px;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 20px;
  border: 1px solid #ed8623;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fff;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${props => `${props.progress}%`};
  height: 100%;
  background: linear-gradient(to right, #f04a23, #ed8623);
  transition: width 0.3s ease-in-out;
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  text-align: center;
  color: #666;
`;

const WelcomeIcon = styled.div`
  font-size: 48px;
  color: #f04a23;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    display: block;
    width: 64px;
    height: 64px;
    background: #f04a23;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
  }
`;

const WelcomeTitle = styled.h2`
  color: #f04a23;
  margin-bottom: 10px;
  font-size: 24px;
`;

const WelcomeText = styled.p`
  font-size: 16px;
  max-width: 500px;
  line-height: 1.6;
`;

function DummySegmentationDataLabellingPage() {
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [niftiFile, setNiftiFile] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [labels] = useState<Label[]>(DUMMY_LABELS);
  const [preLoadedAnnotations] = useState<any>(null);
  const niftiCache = useRef<Map<string, ArrayBuffer>>(new Map());
  
  // Load NIFTI file from public folder
  const fetchNiftiFile = useCallback(async (imageId: string) => {
    try {
      setNiftiFile(null);
      setLoading(true);
      setError(null);
      setDownloadProgress(0);

      // Check cache first
      if (niftiCache.current.has(imageId)) {
        console.log('Loading from cache:', imageId);
        setNiftiFile(niftiCache.current.get(imageId)!);
        setDownloadProgress(100);
        setLoading(false);
        return;
      }

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setDownloadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Fetch the file from public folder
      console.log('Fetching file from:', `/${imageId}`);
      const response = await fetch(`/${imageId}`);
      
      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to load file (${response.status} ${response.statusText}): ${errorText.substring(0, 100)}`);
      }

      // Check content type
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      if (contentType && contentType.includes('text/html')) {
        throw new Error('Received HTML instead of NIFTI file. The file may not exist in the public folder.');
      }

      const arrayBuffer = await response.arrayBuffer();
      
      console.log('Array Buffer Size:', arrayBuffer.byteLength, 'bytes');
      
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('No data received from the file');
      }
      
      // Check if file is suspiciously small (likely HTML error page)
      if (arrayBuffer.byteLength < 1000) {
        const decoder = new TextDecoder();
        const text = decoder.decode(arrayBuffer.slice(0, 200));
        if (text.includes('<!DOCTYPE') || text.includes('<html>')) {
          throw new Error(`File appears to be HTML (${arrayBuffer.byteLength} bytes). The file "${imageId}" may not exist in the public folder.`);
        }
        if (arrayBuffer.byteLength < 348) {
          throw new Error(`File too small (${arrayBuffer.byteLength} bytes). A valid NIFTI file should be at least 348 bytes (header size).`);
        }
      }
      
      clearInterval(progressInterval);
      setDownloadProgress(100);
      
      // Store in cache
      niftiCache.current.set(imageId, arrayBuffer);
      
      setNiftiFile(arrayBuffer);

    } catch (err) {
      console.error('Error fetching NIFTI file:', err);
      setError(err instanceof Error ? err.message : 'Failed to load NIFTI file. Please ensure spleen_2.nii.gz is in the public folder.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-select first image on mount
  useEffect(() => {
    if (DUMMY_IMAGE_IDS.length > 0 && !selectedImageId) {
      setSelectedImageId(DUMMY_IMAGE_IDS[0]);
    }
  }, [selectedImageId]);

  // Load file when image is selected
  useEffect(() => {
    if (selectedImageId) {
      fetchNiftiFile(selectedImageId);
    }
  }, [selectedImageId, fetchNiftiFile]);

  return (
    <PageContainer className="page-container123">
      <Navbar>
        <NavbarTitle>3D CT Segmentation Data Labelling</NavbarTitle>
      </Navbar>
      <ContentWrapper>
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Image List</SidebarTitle>
          </SidebarHeader>
          <ImageListContainer>
            <ImageList>
              {DUMMY_IMAGE_IDS.map((imageId: string) => (
                <ImageListItem
                  key={imageId}
                  isSelected={selectedImageId === imageId}
                  onClick={() => setSelectedImageId(imageId)}
                >
                  <ImageName isSelected={selectedImageId === imageId}>
                    {imageId}
                  </ImageName>
                </ImageListItem>
              ))}
            </ImageList>
          </ImageListContainer>
        </Sidebar>
        <MainContent>
          {loading && (
            <ProgressContainer>
              <ProgressWrapper>
                <ProgressText>Loading NIFTI file... {downloadProgress}%</ProgressText>
                <ProgressBarContainer>
                  <ProgressBar progress={downloadProgress} />
                </ProgressBarContainer>
              </ProgressWrapper>
            </ProgressContainer>
          )}
          {error && (
            <div style={{ 
              color: 'red',
              padding: '20px',
              textAlign: 'center' 
            }}>
              <strong>Error:</strong> {error}
              <br />
              <small style={{ marginTop: '10px', display: 'block' }}>
                Make sure spleen_2.nii.gz is placed in the public folder.
              </small>
            </div>
          )}
          {!loading && !niftiFile && !error && (
            <WelcomeContainer style={{ width: '100%' }}>
              <WelcomeIcon />
              <WelcomeTitle>Welcome to NIFTI Viewer</WelcomeTitle>
              <WelcomeText>
                Select an image from the list on the left to start viewing and analyzing NIFTI files. 
                The viewer supports various visualization options and tools for detailed analysis.
              </WelcomeText>
            </WelcomeContainer>
          )}
          {!loading && niftiFile && (
            <NiftiViewerContainer>
              <NiftiFileReader 
                initialNiftiData={niftiFile} 
                projectId="dummy-project" 
                imageId={selectedImageId ?? ''} 
                labelsFromContext={labels} 
                preLoadedAnnotations={preLoadedAnnotations} 
              />
            </NiftiViewerContainer>
          )}
        </MainContent>
      </ContentWrapper>
    </PageContainer>
  );
}

export default DummySegmentationDataLabellingPage;
